import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useAuditEvents } from '../../graphql/queries';
import {
  DataTable,
  EmptyState,
  PermissionGate,
  TableSkeleton,
  type DataTableColumn,
} from '../../components/shared';
import type { AuditEvent, AuditEventFilter } from '../../graphql/types';
import { AuditAction } from '../../graphql/types';
import { useAppSelector } from '../../store';
import { selectUser } from '../../store/features/auth/authSlice';
import { hasPermission } from '../../lib/permissions';
import { formatDateTime } from '../../lib/dateFormat';
import { exportAuditLog, ApiError } from '../../lib/apiClient';
import '@tylertech/forge/banner';
import '@tylertech/forge/button';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import './index.css';

export const Route = createFileRoute('/audit/')({
  component: AuditLogPage,
});

export { AuditLogPage };

const today = new Date().toISOString().split('T')[0];
const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

const defaultFilter: AuditEventFilter = {
  dateFrom: thirtyDaysAgo,
  dateTo: today,
};

const ACTION_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'All Actions', value: '' },
  { label: 'Created', value: AuditAction.CREATED },
  { label: 'Updated', value: AuditAction.UPDATED },
  { label: 'Deleted', value: AuditAction.DELETED },
  { label: 'Accessed', value: AuditAction.ACCESSED },
  { label: 'Exported', value: AuditAction.EXPORTED },
  { label: 'Granted', value: AuditAction.GRANTED },
  { label: 'Denied', value: AuditAction.DENIED },
];

const columns: DataTableColumn<AuditEvent>[] = [
  {
    key: 'timestamp',
    label: 'Timestamp',
    sortable: true,
    render: (e) => formatDateTime(e.timestamp),
  },
  {
    key: 'actor',
    label: 'Actor',
    render: (e) => <span title={e.actor.email ?? e.actor.id}>{e.actor.name}</span>,
  },
  {
    key: 'action',
    label: 'Action',
    sortable: true,
    render: (e) => (
      <span className={`audit-action-badge audit-action-badge--${e.action}`}>{e.action}</span>
    ),
  },
  {
    key: 'resource',
    label: 'Resource',
    render: (e) => (
      <span>
        {e.resource.type}: {e.resource.name ?? e.resource.id}
      </span>
    ),
  },
  {
    key: 'result',
    label: 'Result',
    render: (e) => (
      <span className={`audit-result-badge audit-result-badge--${e.result.toLowerCase()}`}>
        {e.result}
      </span>
    ),
  },
  {
    key: 'context',
    label: 'Source',
    render: (e) => e.context.source,
  },
];

function AuditLogPage(): JSX.Element {
  const ForgeButton = 'forge-button' as React.ElementType;
  const ForgeBanner = 'forge-banner' as React.ElementType;
  const currentUser = useAppSelector(selectUser);
  const isAdmin = hasPermission(currentUser, 'audit:export');
  const [filter, setFilter] = useState<AuditEventFilter>(defaultFilter);
  const [pendingFilter, setPendingFilter] = useState<AuditEventFilter>(defaultFilter);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [pages, setPages] = useState<AuditEvent[][]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const processedRef = useRef<typeof data>(undefined);

  const { data, loading, error } = useAuditEvents(filter, 100, cursor);
  const nextCursor = data?.auditEvents?.nextCursor ?? undefined;
  const totalCount = data?.auditEvents?.totalCount ?? 0;

  // Accumulate pages — replace on fresh query (cursor undefined), append on load-more
  useEffect(() => {
    if (!loading && data?.auditEvents?.items && data !== processedRef.current) {
      processedRef.current = data;
      setPages((prev) =>
        cursor === undefined ? [data.auditEvents!.items] : [...prev, data.auditEvents!.items]
      );
    }
  }, [data, loading, cursor]);

  const allEvents = pages.flat();

  const handleApplyFilter = (): void => {
    setPages([]);
    processedRef.current = undefined;
    setCursor(undefined);
    setFilter(pendingFilter);
  };

  const handleExport = async (format: 'csv' | 'json'): Promise<void> => {
    setIsExporting(true);
    setExportError(null);
    try {
      const { url } = await exportAuditLog(filter, format);
      window.open(url, '_blank');
    } catch (err) {
      setExportError(
        err instanceof ApiError ? err.message : 'Failed to export audit log. Please try again.'
      );
    } finally {
      setIsExporting(false);
    }
  };

  const AccessDenied = (
    <EmptyState
      icon="lock"
      title="Access Restricted"
      description="Sign in to access the Audit Log."
    />
  );

  return (
    <PermissionGate permission="audit:view" fallback={AccessDenied}>
      <div className="audit-log-page">
        {!isAdmin && (
          <ForgeBanner theme="info" className="audit-scope-banner" role="status">
            <forge-icon name="info" slot="icon" aria-hidden="true"></forge-icon>
            Showing events for your services and teams
          </ForgeBanner>
        )}
        <div className="page-header page-header--toolbar">
          <div className="page-header-title page-header-title--column">
            <h1 className="forge-typography--headline5">Audit Log</h1>
            <p className="forge-typography--body2">
              {loading && allEvents.length === 0
                ? 'Loading…'
                : `Showing ${allEvents.length.toLocaleString()} of ${totalCount.toLocaleString()} event${totalCount !== 1 ? 's' : ''}`}
            </p>
          </div>

          <PermissionGate permission="audit:export">
            <div className="audit-export-actions">
              <ForgeButton
                type="button"
                variant="outlined"
                onClick={() => void handleExport('csv')}
                disabled={isExporting || loading}
              >
                Export CSV
              </ForgeButton>
              <ForgeButton
                type="button"
                variant="outlined"
                onClick={() => void handleExport('json')}
                disabled={isExporting || loading}
              >
                Export JSON
              </ForgeButton>
            </div>
          </PermissionGate>
        </div>

        {exportError && (
          <ForgeBanner theme="error" dismissed={false} className="error-banner" role="alert">
            <div className="banner-content">
              <forge-icon name="error" aria-hidden="true"></forge-icon>
              <span>{exportError}</span>
            </div>
          </ForgeBanner>
        )}

        <div className="audit-filter-panel">
          <div className="audit-filter-row">
            <label className="audit-filter-field">
              <span className="forge-typography--caption">From</span>
              <input
                type="date"
                value={pendingFilter.dateFrom}
                max={pendingFilter.dateTo}
                onChange={(e) => setPendingFilter((f) => ({ ...f, dateFrom: e.target.value }))}
                className="audit-date-input"
                aria-label="Filter from date"
              />
            </label>

            <label className="audit-filter-field">
              <span className="forge-typography--caption">To</span>
              <input
                type="date"
                value={pendingFilter.dateTo}
                min={pendingFilter.dateFrom}
                max={today}
                onChange={(e) => setPendingFilter((f) => ({ ...f, dateTo: e.target.value }))}
                className="audit-date-input"
                aria-label="Filter to date"
              />
            </label>

            <label className="audit-filter-field">
              <span className="forge-typography--caption">Actor</span>
              <input
                type="text"
                placeholder="User ID"
                value={pendingFilter.actorId ?? ''}
                onChange={(e) =>
                  setPendingFilter((f) => ({
                    ...f,
                    actorId: e.target.value || undefined,
                  }))
                }
                className="audit-text-input"
                aria-label="Filter by actor ID"
              />
            </label>

            <label className="audit-filter-field">
              <span className="forge-typography--caption">Entity Type</span>
              <input
                type="text"
                placeholder="e.g. Service"
                value={pendingFilter.entityType ?? ''}
                onChange={(e) =>
                  setPendingFilter((f) => ({
                    ...f,
                    entityType: e.target.value || undefined,
                  }))
                }
                className="audit-text-input"
                aria-label="Filter by entity type"
              />
            </label>

            <label className="audit-filter-field">
              <span className="forge-typography--caption">Action</span>
              <select
                value={pendingFilter.action ?? ''}
                onChange={(e) =>
                  setPendingFilter((f) => ({
                    ...f,
                    action: (e.target.value as AuditAction) || undefined,
                  }))
                }
                className="audit-select-input"
                aria-label="Filter by action"
              >
                {ACTION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>

            <ForgeButton type="button" variant="raised" onClick={handleApplyFilter}>
              Apply
            </ForgeButton>
          </div>
        </div>

        {loading && allEvents.length === 0 ? (
          <TableSkeleton />
        ) : error ? (
          <EmptyState
            icon="error"
            title="Failed to load audit events"
            description={error.message}
          />
        ) : allEvents.length === 0 ? (
          <EmptyState
            icon="search_off"
            title="No audit events found"
            description="Try adjusting the date range or filters."
          />
        ) : (
          <>
            <DataTable columns={columns} data={allEvents} getRowKey={(e) => e.eventId} />
            {nextCursor && (
              <div className="audit-pagination">
                <ForgeButton
                  type="button"
                  variant="outlined"
                  onClick={() => setCursor(nextCursor)}
                  disabled={loading}
                >
                  {loading
                    ? 'Loading…'
                    : `Load more (${(totalCount - allEvents.length).toLocaleString()} remaining)`}
                </ForgeButton>
              </div>
            )}
          </>
        )}
      </div>
    </PermissionGate>
  );
}
